import { Fragment } from "react";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { PrimitiveAtom, useAtom } from "jotai";
import { useTranslation } from "src/i18n";
import { planogramSelectedShelvesAtom } from "src/components/PlanogramExtensions/store/atoms";
import { Color, Flex, Grid, HorizontalDivider, Scroller, SubGrid, Text } from "src/elements";
import { formatDimension } from "src/utils/formatDimension";
import { pogLoad } from "src/components/Planogram/common/shelfWeight";
import {
  formatWeightTotal,
  shelfWeightRows,
} from "src/components/Planogram/common/shelfWeightLabel";
import { resolvePlanogramShelves } from "src/components/Planogram/store/utils";
import {
  formatMerchandisableSpace,
  getMerchandisableSpaceInfo,
} from "src/components/Planogram/common/merchandisableSpace";
import styled from "styled-components";

const CellElement = styled(Flex)`
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${Color.primary};
  padding: 2px 5px;

  &:nth-of-type(odd) {
    border-right: 1px solid ${Color.primary};
  }

  &:nth-last-child(1),
  &:nth-last-child(2) {
    border-bottom: none;
  }
`;

const Cell = ({ children }: { children?: string | number }) => (
  <CellElement>
    <Text variant="caption1" color={Color.primaryActive}>
      {children}
    </Text>
  </CellElement>
);

const SHELF_TYPE_LABELS: Record<PlanogramShelf["type"], string> = {
  REGULAR: "Regular",
  RISER: "Riser",
  HANGCELL: "Hang Cell",
};

type CommonT = ReturnType<typeof useTranslation<"common">>["t"];

const ShelfWeightCells = ({ shelf, t }: { shelf: PlanogramShelf; t: CommonT }) => (
  <>
    {shelfWeightRows(shelf, t).map((row) => (
      <Fragment key={row.label}>
        <Cell>{row.label}</Cell>
        <Cell>{row.value}</Cell>
      </Fragment>
    ))}
  </>
);

const ShelfTable = ({
  shelf,
  isShelfWeightEnabled,
}: {
  shelf: PlanogramShelf;
  isShelfWeightEnabled: boolean;
}) => {
  const { t } = useTranslation("common");
  const width = shelf.getMergedWidth();
  const merchandisableSpace = getMerchandisableSpaceInfo(shelf);

  return (
    <SubGrid gridColumn="1/3" borderColor={Color.primary}>
      <Cell>Shelf</Cell>
      <Cell>{shelf.getName()}</Cell>

      <Cell>Type</Cell>
      <Cell>{SHELF_TYPE_LABELS[shelf.type]}</Cell>

      <Cell>X</Cell>
      <Cell>{formatDimension(shelf.getXLeft())}</Cell>

      <Cell>Y</Cell>
      <Cell>{formatDimension(shelf.getYBottom())}</Cell>

      <Cell>Notch No</Cell>
      <Cell>{shelf.getNotchNo()}</Cell>

      <Cell>Width</Cell>
      <Cell>{formatDimension(width)}</Cell>
      {/* JDA stores thickness as height. */}
      <Cell>Height</Cell>
      <Cell>{formatDimension(shelf.thickness)}</Cell>

      <Cell>Depth</Cell>
      <Cell>{formatDimension(shelf.depth)}</Cell>

      {isShelfWeightEnabled && <ShelfWeightCells shelf={shelf} t={t} />}

      {merchandisableSpace && (
        <>
          <Cell>Merchandisable Space</Cell>
          <Cell>{formatMerchandisableSpace(merchandisableSpace)}</Cell>
        </>
      )}
    </SubGrid>
  );
};

const ShelvesTotalWeight = ({ pog, t }: { pog: Pog; t: CommonT }) => {
  const totalLoadLabel = formatWeightTotal(pogLoad(pog), t);

  return (
    <>
      <HorizontalDivider />

      <Flex justify="space-between" padding="0 0 10px 0">
        <Text variant="small1" color={Color.primary}>
          {t("shelfWeight.pogTotal")}
        </Text>
        <Text variant="small1" color={Color.primaryActive}>
          {totalLoadLabel}
        </Text>
      </Flex>
    </>
  );
};

interface Props {
  pogAtom: PrimitiveAtom<Pog>;
  isShelfWeightEnabled: boolean;
}

export const ShelvesDetails = ({ pogAtom, isShelfWeightEnabled }: Props) => {
  const [pog] = useAtom(pogAtom);
  const [selectedShelfIds] = useAtom(planogramSelectedShelvesAtom);
  const selectedShelves = resolvePlanogramShelves(pog, selectedShelfIds);
  const { t } = useTranslation("common");

  const shelves = pog.planogram.bays.reduce(
    (shelves, bay) => [...shelves, ...bay.shelves],
    [] as PlanogramShelf[],
  );

  return (
    <Scroller gutter="stable both-edges">
      <Flex column gap="10px">
        {selectedShelves.length > 0 && (
          <>
            <Flex column>
              <Text variant="small1" color={Color.primary}>
                {selectedShelves.length > 1 ? t("selectedShelves") : t("selectedShelf")}
              </Text>

              <Grid padding="10px 0" columns="auto 1fr" width="100%" gap="20px 0">
                {selectedShelves.map((shelf) => (
                  <ShelfTable
                    key={shelf.uniqueId}
                    shelf={shelf}
                    isShelfWeightEnabled={isShelfWeightEnabled}
                  />
                ))}
              </Grid>
            </Flex>

            <HorizontalDivider />
          </>
        )}

        <Grid padding="10px 0" columns="auto 1fr" width="100%" gap="20px 0">
          {shelves
            .filter(
              (shelf) => !selectedShelves.some((selected) => selected.uniqueId === shelf.uniqueId),
            )
            .map((shelf) => (
              <ShelfTable
                key={shelf.uniqueId}
                shelf={shelf}
                isShelfWeightEnabled={isShelfWeightEnabled}
              />
            ))}
        </Grid>

        {isShelfWeightEnabled && <ShelvesTotalWeight pog={pog} t={t} />}
      </Flex>
    </Scroller>
  );
};
